import Link from 'next/link';
import { useRouter } from 'next/router';
import { TypographySmall, TypographyText } from '@/components/base/Typography';
import { PROCESS_ORDERS } from '@/constants';
import { mapShape } from '@/constants/index';
import { cn } from '@/utils';
import { clearCart, getCart, removeFromCart } from '@/utils/cart';
import { generateDiamondSlug, generateSettingSlug } from '@/utils/slug';

// Custom SVG icons to match the design
export const DiamondIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 25 24" className={className}>
    <g clipPath="url(#clip0_7496_237)">
      <path
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.35"
        d="M7.412 4.142A.417.417 0 0 1 7.725 4h9.361c.12 0 .235.052.314.142l4.25 4.856a.833.833 0 0 1-.019 1.118l-8.92 9.558a.417.417 0 0 1-.61 0l-8.92-9.558a.833.833 0 0 1-.018-1.118l4.249-4.856Z"
      />
    </g>
  </svg>
);

export const SettingIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 25 24" className={className}>
    <path
      fill="currentColor"
      fillRule="evenodd"
      d="M8.77 2.915a.675.675 0 1 0-.874 1.028l2.131 1.811c-3.294.985-5.693 4.025-5.693 7.622 0 4.395 3.581 7.957 8 7.957 4.418 0 8-3.562 8-7.957 0-3.597-2.4-6.637-5.694-7.622l2.13-1.81a.675.675 0 1 0-.874-1.03L12.925 5.44a8.157 8.157 0 0 0-1.183 0L8.771 2.915Zm2.542 3.93.068.058c.55.468 1.357.468 1.907 0l.068-.057c3.192.488 5.628 3.231 5.628 6.53 0 3.642-2.97 6.607-6.65 6.607-3.679 0-6.65-2.965-6.65-6.607 0-3.299 2.436-6.042 5.629-6.53Z"
      clipRule="evenodd"
    />
  </svg>
);

export const RingIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className={className}>
    <path
      fill="currentColor"
      fillRule="evenodd"
      d="M12 5.32 9.842 3.476l.546-.726h3.248l.53.719L12 5.319ZM8.572 4.166a.8.8 0 0 1-.12-1.089L9.474 1.72a.8.8 0 0 1 .64-.32H13.913a.8.8 0 0 1 .643.325l1.001 1.354a.8.8 0 0 1-.124 1.084l-1.675 1.432a8 8 0 1 1-3.515 0L8.572 4.166Zm3.072 2.594a6.65 6.65 0 1 0 .712 0 .801.801 0 0 1-.712 0Z"
    />
  </svg>
);

export const ProcessStepCard = ({
  className,
  status = 'todo',
  isFirst = false,
  isLast = false,
  href,
  children,
  currentBorderClass = 'border-slate-500',
  ...rest
}) => {
  const CardComponent = href ? Link : 'div';
  const cardProps = href ? { href } : {};

  const bg = '';

  // Determine border classes based on position
  const getBorderClasses = () => {
    const baseBorder = status === 'current' ? currentBorderClass : 'border-gray-300';
    const bgColor = status === 'current' ? 'bg-gray-50' : 'bg-gray-100';

    if (isFirst) {
      // First card: hide right border
      return `border-2 border-r-0 ${bgColor} rounded-l-md ${baseBorder}`;
    } else if (isLast) {
      // Last card: hide left border
      return `border-2 border-l-0 ${bgColor} rounded-r-md ${baseBorder}`;
    } else {
      // Middle card: hide both left and right borders
      return `border-2 border-l-0 ${bgColor} border-r-0 ${baseBorder}`;
    }
  };

  const diagonalBg = status === 'current' ? 'bg-gray-50' : 'bg-gray-100';

  // Compute right-border color for diagonal tips
  const diagonalBorderClass = status === 'current' ? currentBorderClass : 'border-r-gray-300';

  return (
    <div className="relative flex-1">
      <CardComponent {...cardProps} className={cn('block w-full', getBorderClasses())}>
        <div
          {...rest}
          className={cn('relative flex h-19 items-center justify-center px-6 py-4', className)}
        >
          {children}
        </div>

        {/* Right side diagonal elements - only show if not last card */}
        {!isLast && (
          <div className="absolute top-0 -right-3.5 h-20 w-8">
            {/* Top diagonal div */}
            <div
              className={cn(
                'absolute top-0 h-10 w-8 origin-top-right -rotate-20 border-r-2',
                diagonalBorderClass,
                diagonalBg,
              )}
              style={{
                transformOrigin: 'bottom right',
                zIndex: 2,
              }}
            />
            {/* Bottom diagonal div - overlapping to create sharp tip */}
            <div
              className={cn(
                'absolute bottom-0 h-10 w-8 origin-top-right rotate-20 border-r-2',
                diagonalBorderClass,
                diagonalBg,
              )}
              style={{
                transformOrigin: 'top right',
                zIndex: 1,
              }}
            />
          </div>
        )}
      </CardComponent>
    </div>
  );
};

const ProcessStep = ({
  number,
  title,
  line1,
  line2,
  image,
  icon,
  href, // href used for the entire step card
  viewHref, // href used specifically for the "View" link
  status,
  isFirst,
  isLast,
  itemId,
  currentBorderClass,
}) => {
  const router = useRouter();

  const handleRemove = (e) => {
    e.preventDefault(); // Prevent navigation
    if (title === 'Ring' || title === 'Complete') {
      // Restart the process: clear cart and navigate to diamond selection
      clearCart();
      router.push('/diamonds');
      return;
    }

    if (itemId) {
      removeFromCart(itemId);
      router.replace(router.asPath);
    }
  };

  return (
    <ProcessStepCard
      status={status}
      isFirst={isFirst}
      isLast={isLast}
      href={href}
      currentBorderClass={currentBorderClass}
    >
      <div className="flex min-h-[48px] w-full items-center justify-between md:min-h-[64px]">
        {/* Left: step number and details */}
        <div className="flex flex-1 items-center space-x-3">
          {/* Step number – always visible */}
          <TypographyText className="text-2xl font-bold">{number}</TypographyText>

          {/* Details – hide on small screens to reduce clutter */}
          <div className="flex flex-col">
            <TypographyText className="leading-tight font-bold">{title}</TypographyText>
            {line1 || line2 ? (
              <TypographyText className="hidden max-w-[160px] truncate leading-tight whitespace-nowrap md:block">
                {[line1, line2].filter(Boolean).join(' ')}
              </TypographyText>
            ) : (
              <TypographyText className="hidden leading-tight md:block">
                Choose your {title.toLowerCase()}
              </TypographyText>
            )}
          </div>
        </div>

        {/* Right: actions and thumbnail */}
        <div className="flex items-center space-x-4">
          {/* Action links – hide on small screens */}
          {itemId && (
            <Link href={viewHref || href} className="hidden p-0 text-slate-600 underline md:block">
              <TypographyText>View</TypographyText>
            </Link>
          )}
          {itemId && (viewHref || href) && (
            <TypographySmall className="text-muted-foreground hidden md:block">|</TypographySmall>
          )}
          {itemId && (
            <button
              onClick={handleRemove}
              className="hidden bg-transparent p-0 text-slate-600 underline hover:bg-transparent md:block"
            >
              <TypographyText>Remove</TypographyText>
            </button>
          )}

          {/* Thumbnail always visible */}
          <div className="flex h-10 w-10 items-center justify-center">
            {image ? (
              <img src={image} alt="thumbnail" className="h-10 w-10 rounded-sm object-cover" />
            ) : (
              icon
            )}
          </div>
        </div>
      </div>
    </ProcessStepCard>
  );
};

export const ProcessSteps = ({ currentStep: propCurrent, currentBorderClass }) => {
  const router = useRouter();

  // Determine current step from prop or pathname
  let currentStep = propCurrent;
  if (!currentStep) {
    const path = router.pathname;
    // Treat both natural diamond and lab-grown diamond pages as the "diamond" step
    if (
      path.startsWith('/diamond') ||
      path.startsWith('/diamonds') ||
      path.startsWith('/lab-diamond') ||
      path.startsWith('/lab-diamonds')
    )
      currentStep = 'diamond';
    else if (path.startsWith('/ring') || path.startsWith('/rings')) currentStep = 'rings';
    else if (path.startsWith('/cart')) currentStep = 'ring';
  }

  // Read cart (client-side only)
  const cart = typeof window !== 'undefined' ? getCart() : [];

  // Separate diamond vs setting items
  const diamondItems = cart.filter((i) => i.itemType !== 'ring');
  const ringItems = cart.filter((i) => i.itemType === 'ring');

  // Earliest of each type
  const firstDiamond = diamondItems.sort((a, b) => new Date(a.addedAt) - new Date(b.addedAt))[0];
  const firstRing = ringItems.sort((a, b) => new Date(a.addedAt) - new Date(b.addedAt))[0];

  // Establish ordering based on cart and current page
  let order;
  if (firstDiamond && firstRing) {
    order =
      new Date(firstDiamond.addedAt) < new Date(firstRing.addedAt)
        ? PROCESS_ORDERS.DIAMOND_FIRST
        : PROCESS_ORDERS.RING_FIRST;
  } else if (firstDiamond) {
    order = PROCESS_ORDERS.DIAMOND_FIRST;
  } else if (firstRing) {
    order = PROCESS_ORDERS.RING_FIRST;
  } else {
    order = currentStep === 'rings' ? PROCESS_ORDERS.RING_FIRST : PROCESS_ORDERS.DIAMOND_FIRST;
  }

  // Build steps array dynamically
  const steps = order.map((type, idx) => {
    const isDiamond = type === 'diamond';
    const item = isDiamond ? firstDiamond : firstRing;

    // Default title when no item yet
    const defaultTitle = isDiamond ? 'Diamond' : 'Setting';

    // Determine status
    let status = 'todo';
    if (item) status = currentStep === type ? 'current' : 'done';
    else if (currentStep === type) status = 'current';

    // Build display lines if item available
    let line1, line2;
    if (item) {
      if (isDiamond) {
        line1 = `${item.carats} ct`;
        line2 = mapShape(item.shape);
      } else {
        line1 = item.productName || 'Setting';
        line2 = `${item.metal || ''} ${item.shape || ''}`.trim();
      }
    }

    // Determine href for the product detail (used in "View" link)
    let viewHref;
    if (item) {
      if (isDiamond) {
        const basePath = item.labgrown ? '/lab-diamond' : '/diamond';
        const shapeSlug = (item.shape || '').toLowerCase();
        const slug = generateDiamondSlug({
          diamondId: item.diamondId,
          shape: item.shape,
          carats: item.carats,
          col: item.color,
          clar: item.clarity,
          cut: item.cut,
          lab: item.lab,
          labgrownType: item.labgrown ? 'lab' : undefined,
        });
        viewHref = `${basePath}/${shapeSlug}/${slug}`;
      } else {
        const baseId = item.productId || item.diamondId;
        const numericId = String(baseId).split('/').pop();
        const slug = generateSettingSlug({ id: numericId, name: item.productName || '' });
        viewHref = `/ring/${slug}`;
      }
    }

    // Determine href for the entire step card (listing page)
    let cardHref;
    if (isDiamond) {
      if (item) {
        // If the selected diamond is lab-grown then route to lab-diamonds, otherwise natural diamonds
        cardHref = item.labgrown ? '/lab-diamonds' : '/diamonds';
      } else {
        cardHref = '/diamonds';
      }
    } else {
      cardHref = '/rings';
    }

    return {
      number: (idx + 1).toString(),
      title: defaultTitle,
      line1,
      line2,
      icon: isDiamond ? <DiamondIcon className="h-6 w-6" /> : <SettingIcon className="h-6 w-6" />,
      href: cardHref,
      viewHref,
      image: item?.image,
      status,
      itemId: item?.diamondId,
    };
  });

  // Third step – Checkout/Ring
  const thirdStatus = router.pathname.startsWith('/cart')
    ? 'current'
    : firstDiamond && firstRing
      ? 'done'
      : 'todo';

  const showRingDetails = firstDiamond && firstRing; // Only show details when both selections are made

  // Determine viewHref for the ring step (used in "View" link)
  let ringViewHref;
  if (showRingDetails && firstRing) {
    // Once both selections are made, the view should navigate to the cart
    ringViewHref = '/cart';
  }

  steps.push({
    number: '3',
    title: 'Complete',
    icon: <RingIcon className="h-6 w-6" />,
    // The third step should only be clickable when both a diamond and a setting have been chosen
    href: showRingDetails ? '/cart' : undefined,
    image: showRingDetails ? firstRing?.image : undefined,
    status: thirdStatus,
    itemId: showRingDetails ? firstRing?.diamondId : undefined,
    line1: 'Complete your ring',
    ...(ringViewHref ? { viewHref: ringViewHref } : {}),
  });

  return (
    <div className="flex w-full overflow-x-auto">
      {steps.map((step, index) => (
        <ProcessStep
          key={step.number}
          {...step}
          isFirst={index === 0}
          isLast={index === steps.length - 1}
          currentBorderClass={currentBorderClass}
        />
      ))}
    </div>
  );
};
